import { CModal } from "@coreui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import BoxWrapper from "../../components/ui/BoxWrapper";
import Pagination from "../../components/ui/Pagination";
import RenderTable from "../../components/ui/RenderTable";
import { useCookie } from "../../hooks/useCookie";
import { useDebounce } from "../../hooks/useDebounce";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import AdminRoute from "../auth/RestrictedRoute";
import NewUser from "./NewPlaceAdminPartner";
import PlaceAdminPartnersTable from "./placeAdminPartnersTable";
import { apiConfig } from "../../config/apiConfig";
import { ROLES } from "../../config/constants";
import { QrCodeIcon } from "lucide-react";
import { Link } from "react-router-dom";

const PAGE_SIZE = 10;

export default function PlaceAdminPartners() {
  const [visible, setVisible] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const { token } = useCookie("vToken");

  const search = useDebounce(searchQuery, 2000);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const endpoint = apiConfig.users.placeManagers.list();
  const searchDependencies = ["getMultiPlaceAdminPartners"];
  const { data, isLoading } = useQuery(
    searchDependencies,
    () => {
      return apiService.MakeGetRequest(endpoint, token);
    },
    { keepPreviousData: true },
  );

  const UserSVG = () => {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="white"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M16.1583 8.23285C16.1583 10.5825 14.2851 12.4666 11.949 12.4666C9.61292 12.4666 7.73974 10.5825 7.73974 8.23285C7.73974 5.88227 9.61292 4 11.949 4C14.2851 4 16.1583 5.88227 16.1583 8.23285ZM11.949 20C8.51785 20 5.58809 19.456 5.58809 17.2802C5.58809 15.1034 8.49904 14.5396 11.949 14.5396C15.3802 14.5396 18.31 15.0836 18.31 17.2604C18.31 19.4362 15.399 20 11.949 20ZM17.9571 8.30922C17.9571 9.50703 17.5998 10.6229 16.973 11.5505C16.9086 11.646 16.9659 11.7748 17.0796 11.7946C17.2363 11.8216 17.3984 11.8369 17.5631 11.8414C19.2062 11.8846 20.6809 10.821 21.0883 9.21974C21.6918 6.84123 19.9198 4.7059 17.6634 4.7059C17.4181 4.7059 17.1835 4.73201 16.9551 4.77884C16.9238 4.78605 16.8907 4.80046 16.8728 4.82838C16.8513 4.8626 16.8674 4.90853 16.8889 4.93825C17.5667 5.8938 17.9571 7.05918 17.9571 8.30922ZM20.6782 13.5126C21.7823 13.7296 22.5084 14.1727 22.8093 14.8166C23.0636 15.3453 23.0636 15.9586 22.8093 16.4864C22.349 17.4851 20.8654 17.8058 20.2887 17.8886C20.1696 17.9066 20.0738 17.8031 20.0864 17.6833C20.3809 14.9157 18.0377 13.6035 17.4315 13.3018C17.4055 13.2883 17.4002 13.2676 17.4028 13.255C17.4046 13.246 17.4154 13.2316 17.4351 13.2289C18.7468 13.2046 20.1571 13.3847 20.6782 13.5126ZM6.43711 11.8413C6.60186 11.8368 6.76304 11.8224 6.92063 11.7945C7.03434 11.7747 7.09165 11.6459 7.02718 11.5504C6.4004 10.6228 6.04313 9.50694 6.04313 8.30913C6.04313 7.05909 6.43353 5.89371 7.11135 4.93816C7.13284 4.90844 7.14806 4.86251 7.12746 4.82829C7.10956 4.80127 7.07553 4.78596 7.04509 4.77875C6.81586 4.73192 6.58127 4.70581 6.33593 4.70581C4.07951 4.70581 2.30751 6.84114 2.91191 9.21965C3.31932 10.8209 4.79405 11.8845 6.43711 11.8413ZM6.59694 13.2545C6.59962 13.268 6.59425 13.2878 6.56918 13.3022C5.9621 13.6039 3.61883 14.9161 3.91342 17.6827C3.92595 17.8034 3.83104 17.9061 3.71195 17.889C3.13531 17.8061 1.65163 17.4855 1.19139 16.4867C0.936203 15.9581 0.936203 15.3457 1.19139 14.817C1.49225 14.1731 2.21752 13.73 3.32156 13.512C3.84358 13.385 5.25294 13.2049 6.5656 13.2292C6.5853 13.2319 6.59515 13.2464 6.59694 13.2545Z"
        />{" "}
      </svg>
    );
  };

  return (
    <AdminRoute route={ROLES.PLACE_ADMIN} requireMultiPlaceAdmin={true}>
      <h5 className="fs-4 fw-bold mb-2">Mes Partenaires</h5>
      <BoxWrapper>
        <div className="d-flex  justify-content-between align-items-baseline before-table">
          <div className="d-flex align-items-center gap-2 p-3 delete-add">
            <button
              onClick={() => setVisible(!visible)}
              className="outline-none border-0 btn btn-primary rounded-pill text-white d-flex align-items-center gap-2 justify-content-center shadow-secondary"
            >
              + Ajouter un partenaire
            </button>
            <Link
              to="/place-admin-partners/qr-codes/place"
              className="outline-none  btn btn-outline-danger rounded-pill text-danger d-flex align-items-center gap-2 justify-content-center shadow-primary"
            >
              <QrCodeIcon /> Codes QR
            </Link>
            <CModal
              size="lg"
              alignment="center"
              visible={visible}
              onClose={() => setVisible(false)}
            >
              <NewUser
                open={visible}
                onAccept={() => {}}
                onClose={() => setVisible(false)}
              />
            </CModal>
          </div>
          <div className="search-form">
            <div className=" input-field">
              <MagnifyingGlassIcon color="gray" width={20} />
              <input
                type="text"
                className="w-50 border-0 outline-none rounded-sm p-1 text-gray fs-6"
                placeholder="Rechercher"
                onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
              />
            </div>
          </div>
          <div className="d-flex flex-column-reverse flex-md-row  justify-content-end">
            <div className="d-flex align-items-center justify-content-between">
              <div
                className="d-flex align-items-center justify-content-center gap-3 bg-gradient-primary p-3 w-100"
                style={{ borderTopRightRadius: "8px" }}
              >
                <UserSVG />
                <p className="fs-5 fw-bold m-0  text-white">
                  {data?.meta?.totalItems}{" "}
                  {data?.meta?.totalItems > 1 ? "Partenaires" : "Partenaire"}
                </p>
              </div>
            </div>
          </div>
        </div>
        <RenderTable
          loading={isLoading}
          render={() =>
            data?.items?.length > 0 ? (
              <PlaceAdminPartnersTable users={data?.items as User[]} />
            ) : (
              <p className="text-center fs-5 my-2">{general.fr.noData}</p>
            )
          }
        />
      </BoxWrapper>
      <Pagination
        currentPage={currentPage}
        onPageChange={(page) => {
          setCurrentPage(page);
        }}
        siblingCount={0}
        totalCount={data?.meta?.totalPages}
        pageSize={PAGE_SIZE}
      />
    </AdminRoute>
  );
}
